<template>
  <PremiumFeature feature="projects" :limit="projects?.length">
    <Dialog v-model:open="newProjectDialogOpen">
      <DialogTrigger as-child>
        <Button>
          <template #startIcon>
            <Plus />
          </template>
          New Project
        </Button>
      </DialogTrigger>
      <DialogContent class="max-sm:h-2/3">
        <DialogHeader>
          <DialogTitle>Create new project</DialogTitle>
        </DialogHeader>
        <div
          class="md:flex gap-2 bg-primary-700 rounded-b-lg md:overflow-hidden"
        >
          <div class="bg-primary-800 py-4 md:w-[260px] space-y-4">
            <div class="px-4">
              <Input
                v-model="searchQuery"
                type="text"
                variant="dark"
                placeholder="Search templates..."
              />
            </div>
            <Tabs v-model="selectedTab" orientation="vertical">
              <TabsList>
                <TabsTrigger
                  value="all"
                  class="text-left data-[state=active]:bg-neutral-300/[0.08] data-[state=active]:text-accent-400 py-3.5 px-2 text-sm"
                  :disabled="isLoading"
                >
                  All templates
                </TabsTrigger>
                <TabsTrigger
                  value="examples"
                  class="text-left data-[state=active]:bg-neutral-300/[0.08] data-[state=active]:text-accent-400 py-3.5 px-2 text-sm"
                  :disabled="isLoading"
                >
                  Examples
                </TabsTrigger>
                <TabsTrigger
                  value="game-engines"
                  class="text-left data-[state=active]:bg-neutral-300/[0.08] data-[state=active]:text-accent-400 py-3.5 px-2 text-sm"
                  :disabled="isLoading"
                >
                  Game Engines
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <div
            ref="scrollContainer"
            class="relative overflow-auto md:max-h-[436px]"
          >
            <div
              v-if="isLoading"
              class="md:w-[440px] p-4 md:min-h-[436px] flex items-center justify-center"
            >
              <Spinner />
            </div>
            <template v-else>
              <TransitionGroup
                v-if="filteredProjects.length > 0"
                name="project-list"
                tag="div"
                class="md:flex md:flex-wrap md:w-[440px] p-4 md:min-h-[436px]"
                enter-active-class="transition-all duration-300 ease-in-out"
                leave-active-class="transition-all duration-300 ease-in-out"
                move-class="transition-all duration-300 ease-in-out"
                enter-from-class="opacity-0 transform scale-90"
                leave-to-class="opacity-0 transform scale-90"
              >
                <ProjectPlank
                  v-for="(project, index) in filteredProjects"
                  :key="'project-' + index"
                  :class="
                    cn(
                      'md:w-1/2 transition-all duration-300 ease-in-out transform'
                    )
                  "
                  :title="project.title"
                  :description="project.description"
                  :img="
                    project.img ? getTemplateImageUrl(project.img) : undefined
                  "
                  :icon="project.icon"
                  :categories="
                    project.categories.map(category => category.label)
                  "
                  @click="!isLoading && create(index)"
                />
              </TransitionGroup>

              <div v-else class="flex justify-center w-[440px] pt-4">
                <p
                  class="text-sm p-4 text-neutral-400 bg-primary-800 rounded-lg"
                >
                  No templates found. Try adjusting your search or filter.
                </p>
              </div>
            </template>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    <template #fallback>
      <UpgradeModal>
        <template #trigger>
          <Button>
            <template #startIcon>
              <Plus />
            </template>
            New Project
          </Button>
        </template>
        <template #upgradeMessage>
          <Alert
            title="You have already reached your 3 project limit for this workspace"
            subtitle="Upgrade to any of our plans to unlock unlimited projects"
            class="mb-6"
          />
        </template>
      </UpgradeModal>
    </template>
  </PremiumFeature>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import { Plus } from 'lucide-vue-next';
import { useStore } from 'vuex';
import { useQuery, useMutation } from '@tanstack/vue-query';
import { toast } from 'vue-sonner';
import { cn } from '@ui/utils';

import Spinner from '@ui/components/Spinner.vue';

import { Alert } from '@ui/components/alert';
import { Button } from '@ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
  DialogHeader,
} from '@ui/components/dialog';

import requests from '@vuex/requests.js';

import PremiumFeature from '@hoc/PremiumFeature.vue';
import { UpgradeModal } from '@components/common/UpgradeModal';

import { TEMPLATE_PROJECTS } from '@components/project-list/constants.js';
import ProjectPlank from '@components/project-list/ProjectPlank.vue';

import { Tabs, TabsList, TabsTrigger } from '@ui/components/tabs';
import Input from '@/ui/components/input/Input.vue';
import { queries } from '@/queries/index.js';
import { OverlayScrollbars } from 'overlayscrollbars';
import 'overlayscrollbars/overlayscrollbars.css';

const isLoading = ref(false);
const selectedTab = ref('all');
const templateProjects = ref(TEMPLATE_PROJECTS);
const searchQuery = ref('');
const scrollContainer = ref(null);
let osInstance = null;

const initializeOverlayScrollbars = () => {
  if (scrollContainer.value) {
    osInstance = OverlayScrollbars(scrollContainer.value, {
      scrollbars: {
        autoHide: 'scroll',
        autoHideDelay: 500,
      },
    });
  } else {
    console.error('scrollContainer is null');
  }
};

onMounted(() => {
  const observer = new MutationObserver((mutations, obs) => {
    const container = scrollContainer.value;
    if (container) {
      initializeOverlayScrollbars();
      obs.disconnect(); // Stop observing once we've initialized
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
});

onBeforeUnmount(() => {
  if (osInstance) {
    osInstance.destroy();
  }
});

const filteredProjects = computed(() => {
  let filtered = templateProjects.value;

  // First, filter by tab selection
  if (selectedTab.value !== 'all') {
    filtered = filtered.filter(project =>
      project.categories.some(category => category.id === selectedTab.value)
    );
  }

  // Then, filter by search query
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    filtered = filtered.filter(project =>
      project.title.toLowerCase().includes(query)
    );
  }

  return filtered;
});

watch(searchQuery, () => {
  if (searchQuery.value) {
    selectedTab.value = 'all';
  }
});

const router = useRouter();
const store = useStore();

const newProjectDialogOpen = ref(false);

const getTemplateImageUrl = file => {
  return store.getters['project/data/assets/getTemplateImageUrl'](file);
};

const { data: projects } = useQuery(queries.workspace.projects);

const { mutate: createProject } = useMutation({
  mutationFn: async payload => {
    const newProject = await requests.workspaces.projects.create({
      workspaceId: store.getters['workspace/getId'],
      ...payload,
    });
    return newProject;
  },
  onSuccess: async data => {
    await store.dispatch('application/channels/leaveProjectUpdateChannel', {
      hash: data.hash,
    });
    await store.dispatch(
      'application/channels/leaveProjectCollaborateChannel',
      {
        hash: data.hash,
      }
    );
    await store.dispatch('loadProjects');
    await router.push({ name: 'project', params: { hash: data.hash } });
  },
  onError: async error => {
    const { title: message } = error.response.data;
    toast.error(message || 'Something went wrong, please try again later');
    newProjectDialogOpen.value = false;
  },
});

const create = index => {
  createProject({ template: templateProjects.value[index].id });
  isLoading.value = true;
};
</script>
